import { useCallback, useEffect, useRef, useState } from 'react'

import { useRealtimeFrames, useRealtimeResync } from '@/lib/realtime'

import { notificationsApi } from '../api'
import {
  isInboxNotification,
  isUnread,
  parseNotification,
  type Notification,
} from '../types'

export type NotificationsPhase = 'loading' | 'ready' | 'error'

export interface NotificationsStatus {
  tone: 'success' | 'error'
  messageKey: string
}

export interface NotificationsState {
  items: Notification[]
  phase: NotificationsPhase
  refreshing: boolean
  error: unknown
  status: NotificationsStatus | null
  markingAll: boolean
  refresh: () => void
  markRead: (id: string) => Promise<boolean>
  markAllRead: () => Promise<void>
  dismissStatus: () => void
}

/**
 * Explicit request lifecycle for the inbox (D-042). A manual refresh keeps the
 * previous rows visible and exposes `refreshing`, while the first load uses the
 * skeleton state. Reads are optimistic and reconciled from the server response;
 * a failure restores the previous row and reports a catalog key.
 */
export function useNotifications(): NotificationsState {
  const [items, setItems] = useState<Notification[]>([])
  const [phase, setPhase] = useState<NotificationsPhase>('loading')
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [status, setStatus] = useState<NotificationsStatus | null>(null)
  const [markingAll, setMarkingAll] = useState(false)
  const [attempt, setAttempt] = useState(0)
  const loadedRef = useRef(false)

  useEffect(() => {
    const controller = new AbortController()
    notificationsApi
      .list(controller.signal)
      .then((response) => {
        if (controller.signal.aborted) return
        setItems(response.items.filter(isInboxNotification))
        setPhase('ready')
        setRefreshing(false)
        setError(null)
        loadedRef.current = true
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return
        setError(cause)
        setRefreshing(false)
        setPhase(loadedRef.current ? 'ready' : 'error')
      })
    return () => controller.abort()
  }, [attempt])

  const refresh = useCallback(() => {
    setStatus(null)
    setError(null)
    if (loadedRef.current) {
      setRefreshing(true)
    } else {
      setPhase('loading')
    }
    setAttempt((value) => value + 1)
  }, [])

  // Background reconciler for focus/reconnect: keeps rows visible and never
  // flashes the explicit refresh indicator (D-042/D-053).
  const sync = useCallback(() => {
    setAttempt((value) => value + 1)
  }, [])

  useRealtimeFrames((frame) => {
    if (frame.type !== 'notification') return
    const notification = parseNotification(frame.notification)
    if (!notification || !isInboxNotification(notification)) return
    setItems((current) => {
      if (current.some((row) => row.id === notification.id)) return current
      return [notification, ...current].sort(
        (a, b) =>
          Date.parse(b.createdAt) - Date.parse(a.createdAt) ||
          b.id.localeCompare(a.id),
      )
    })
  })

  useRealtimeResync(sync)

  useEffect(() => {
    function onFocus() {
      if (document.visibilityState === 'visible') sync()
    }
    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onFocus)
    return () => {
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onFocus)
    }
  }, [sync])

  const markRead = useCallback(
    async (id: string): Promise<boolean> => {
      const current = items.find((item) => item.id === id)
      if (!current || !isUnread(current)) return true

      setStatus(null)
      setItems((rows) =>
        rows.map((row) =>
          row.id === id ? { ...row, readAt: new Date().toISOString() } : row,
        ),
      )
      try {
        await notificationsApi.markRead(id)
        return true
      } catch {
        setItems((rows) =>
          rows.map((row) =>
            row.id === id && row.readAt !== null
              ? { ...row, readAt: null }
              : row,
          ),
        )
        setStatus({
          tone: 'error',
          messageKey: 'notifications.status.markReadError',
        })
        return false
      }
    },
    [items],
  )

  const markAllRead = useCallback(async () => {
    if (markingAll) return
    const previous = items
    const hasUnread = previous.some(isUnread)
    if (!hasUnread) return

    setMarkingAll(true)
    setStatus(null)
    const readAt = new Date().toISOString()
    setItems((rows) =>
      rows.map((row) => (isUnread(row) ? { ...row, readAt } : row)),
    )
    try {
      await notificationsApi.markAllRead()
      setStatus({
        tone: 'success',
        messageKey: 'notifications.status.markedAllRead',
      })
    } catch {
      setItems(previous)
      setStatus({
        tone: 'error',
        messageKey: 'notifications.status.markAllError',
      })
    } finally {
      setMarkingAll(false)
    }
  }, [items, markingAll])

  const dismissStatus = useCallback(() => setStatus(null), [])

  return {
    items,
    phase,
    refreshing,
    error,
    status,
    markingAll,
    refresh,
    markRead,
    markAllRead,
    dismissStatus,
  }
}
